import {
    ChangeDetectorRef, DestroyRef, Directive, inject, OnInit
} from "@angular/core";
import {
    MatSort, Sort
} from "@angular/material/sort";
import { LIST_STATE_SERVICE } from "../constants/tokens";
import { IListStateService } from "../interfaces/list-state-service";
import { IListSortOption } from "../interfaces/list-sort-option";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";
import { IAppService } from "../../application/interfaces/app-service";
import { InvalidControlStateException } from "../../exceptions/invalid-control-state-exception";
import { ProcessingDirective } from "../../application/directives/processing/processing.directive";

/**
 * Directive that connects a MatSort to a ListStateService for sorting.
 *
 * This directive synchronizes the sort state between the MatSort component
 * and the application's list state service. It automatically disables the
 * clear functionality to ensure a sort is always applied.
 *
 * ## Features
 * - Bidirectional synchronization between MatSort and ListStateService
 * - Automatic disabling of sort clear functionality
 * - Error handling for invalid sort configurations
 * - Processing state integration via ProcessingDirective
 *
 * ## Dependencies
 * - Requires a LIST_STATE_SERVICE provider in the component's injector hierarchy
 * - Requires a COMMON_APP_SERVICE provider for error handling and notifications
 * - Must be used with Angular Material's MatSort directive
 *
 * ## Usage
 * ```html
 * <mat-table #table matSort mgdSort class="mat-table-list" [dataSource]="dataSource">
 *   <ng-container matColumnDef="name">
 *     <mat-header-cell *matHeaderCellDef mat-sort-header>
 *       Name
 *     </mat-header-cell>
 *     <mat-cell *matCellDef="let row">
 *       {{ row.name }}
 *     </mat-cell>
 *   </ng-container>
 *
 *   <ng-container matColumnDef="description">
 *     <mat-header-cell *matHeaderCellDef mat-sort-header>
 *       Description
 *     </mat-header-cell>
 *     <mat-cell *matCellDef="let row">
 *       {{ row.description }}
 *     </mat-cell>
 *   </ng-container>
 *
 *   <mat-header-row *matHeaderRowDef="displayedColumns"></mat-header-row>
 *   <mat-row *matRowDef="let row; columns: displayedColumns;"></mat-row>
 * </mat-table>
 * ```
 *
 * @see {@link IListStateService} for state management interface
 * @see {@link ProcessingDirective} for processing state handling
 */
@Directive({
    selector: "[mgdSort]",
    hostDirectives: [{
        directive: ProcessingDirective,
        inputs: ["disableWhileProcessing"]
    }]
})
export class SortDirective implements OnInit {

    /** Application service providing error handling and notification services */
    private readonly _app = inject(COMMON_APP_SERVICE) as IAppService;

    /** Angular Material sort directive instance attached to the same element */
    private readonly _sort = inject(MatSort, { self: true });

    /** List state service that manages sorting state and options */
    private readonly _stateService = inject(LIST_STATE_SERVICE) as IListStateService;

    /** Destroy reference for automatic subscription cleanup */
    private readonly _destroyRef = inject(DestroyRef);

    /** Change detector reference for manual change detection triggering */
    private readonly _cdRef = inject(ChangeDetectorRef);

    /**
     * Initializes the directive by setting up bidirectional synchronization
     * between MatSort and the ListStateService.
     *
     * This method:
     * 1. Disables the clear functionality on MatSort to ensure a sort is always applied
     * 2. Sets up subscription to MatSort changes to update the state service
     * 3. Sets up subscription to state service changes to update MatSort
     * 4. Handles error cases when sort options don't match
     */
    ngOnInit(): void {
        this._initializeSortConfiguration();
        this._setupMatSortToStateSync();
        this._setupStateToMatSortSync();
    }

    /**
     * Configures the MatSort instance with required settings.
     * Disables the clear option to ensure a sort is always applied.
     */
    private _initializeSortConfiguration(): void {
        this._sort.disableClear = true;
    }

    /**
     * Sets up synchronization from MatSort changes to the state service.
     * When the user clicks on sort headers, this updates the application state.
     */
    private _setupMatSortToStateSync(): void {
        this._sort.sortChange
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((sort: Sort) => {
                const matchingSortOption = this._findMatchingSortOption(sort);

                if (matchingSortOption) {
                    this._stateService.updateSort(matchingSortOption);
                }
                else {
                    this._handleSortError(sort);
                }
            });
    }

    /**
     * Sets up synchronization from state service changes to MatSort.
     * When the application state changes, this updates the MatSort display.
     */
    private _setupStateToMatSortSync(): void {
        this._stateService.sort$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((sortOption) => {
                if (this._shouldUpdateMatSort(sortOption)) {
                    this._updateMatSort(sortOption);
                }
            });
    }

    /**
     * Finds a matching sort option in the state service for the given MatSort state.
     *
     * @param sort - The sort state from MatSort
     * @returns The matching sort option or undefined if not found
     */
    private _findMatchingSortOption(sort: Sort): IListSortOption | undefined {
        return this._stateService.sortOptions?.find((option) =>
            option.columnSort?.active === sort.active
            && option.columnSort?.direction === sort.direction);
    }

    /**
     * Handles error cases when a sort option cannot be found.
     *
     * @param sort - The sort state that caused the error
     */
    private _handleSortError(sort: Sort): void {
        const errorMessage = `sortOption for ${sort.active} ${sort.direction} not found`;
        this._app.errorHandler.handleDirectiveError(
            new InvalidControlStateException(errorMessage)
        );
        this._app.notificationService.displayErrorMessage(
            `Unable to sort by ${sort.active}`
        );
    }

    /**
     * Determines if MatSort should be updated based on the current state.
     *
     * @param sortOption - The new sort option from the state service
     * @returns True if MatSort needs to be updated
     */
    private _shouldUpdateMatSort(sortOption: any): boolean {
        if (!sortOption) {
            return false;
        }
        return !(
            this._sort?.active === sortOption?.columnSort?.active
            && this._sort?.direction === sortOption?.columnSort?.direction
        );
    }

    /**
     * Updates the MatSort instance with new sort values and triggers change detection.
     *
     * @param sortOption - The sort option containing the new values
     */
    private _updateMatSort(sortOption: any): void {
        if (sortOption) {
            this._sort.active = sortOption.columnSort?.active;
            this._sort.direction = sortOption.columnSort?.direction;
            this._cdRef.markForCheck();
        }
    }
}
